import {createSlice, type PayloadAction} from "@reduxjs/toolkit";

import type {PoolStatistics, PoolStatisticsMode} from "../api/payload-types/pool-statistics";
import {buildApiQueryError, type QueryError} from "../util/errorUtil";
import type {ApiQueryError} from "./types";

/** Selectable time-unit modes of the pool statistics, in display order. Default = WEEKS. */
export const POOL_STATISTICS_MODES: PoolStatisticsMode[] = ["WEEKS", "MONTHS", "DYNAMIC"];

interface PoolStatisticsState {
    /** True once the first load has been triggered (drives the on-demand init no-op). */
    initialized: boolean;
    loading: boolean;
    /** Currently selected time-unit mode. */
    mode: PoolStatisticsMode;
    /** Loaded pool snapshot per mode — cached so switching modes back and forth is instant. */
    statistics: Partial<Record<PoolStatisticsMode, PoolStatistics>>;
    error: ApiQueryError | null;
}

const initialState: PoolStatisticsState = {
    initialized: false,
    loading: false,
    mode: "WEEKS",
    statistics: {},
    error: null,
};

export const poolStatisticsSlice = createSlice({
    name: "poolStatistics",
    initialState,
    reducers: {
        resetPoolStatistics: () => initialState,
        setPoolStatisticsMode: (state, action: PayloadAction<PoolStatisticsMode>) => {
            state.mode = action.payload;
        },
        setPoolStatisticsLoading: (state) => {
            state.initialized = true;
            state.loading = true;
            state.error = null;
        },
        setPoolStatisticsLoaded: (state, action: PayloadAction<{mode: PoolStatisticsMode; data: PoolStatistics}>) => {
            state.loading = false;
            state.statistics[action.payload.mode] = action.payload.data;
        },
        setPoolStatisticsError: (state, action: PayloadAction<QueryError>) => {
            state.loading = false;
            state.error = buildApiQueryError(action.payload);
        },
        clearPoolStatisticsCache: (state) => {
            state.statistics = {};
        },
    },
    selectors: {
        selectPoolStatisticsInitialized: (state) => state.initialized,
        selectPoolStatisticsLoading: (state) => state.loading,
        selectPoolStatisticsMode: (state) => state.mode,
        selectCurrentPoolStatistics: (state) => state.statistics[state.mode] ?? null,
        selectPoolStatisticsCache: (state) => state.statistics,
        selectPoolStatisticsError: (state) => state.error,
    },
});

export const {
    resetPoolStatistics,
    setPoolStatisticsMode,
    setPoolStatisticsLoading,
    setPoolStatisticsLoaded,
    setPoolStatisticsError,
    clearPoolStatisticsCache,
} = poolStatisticsSlice.actions;

export const {
    selectPoolStatisticsInitialized,
    selectPoolStatisticsLoading,
    selectPoolStatisticsMode,
    selectCurrentPoolStatistics,
    selectPoolStatisticsCache,
    selectPoolStatisticsError,
} = poolStatisticsSlice.selectors;

export default poolStatisticsSlice.reducer;
